// src/components/AccountPage.jsx
import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// F11 fix (2 Jun): fresh token per request (raw fetch, not via api.js).
async function freshToken() {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

const GOLD = '#C8A84B';
const DARK = '#0D1B2A';

const API = 'https://3z7hi3z1la.execute-api.ap-southeast-2.amazonaws.com/prod';

const scoreColor = s => s >= 8 ? '#3A9A58' : s >= 6 ? '#B07820' : '#C04040';

export default function AccountPage({ token, user, onBack, onSignOut }) {
  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals,  setTotals]  = useState({ count: 0, spend: 0 });

  useEffect(() => {
    async function load() {
      try {
        // Load all matters with their runs for billing summary
        const t = await freshToken();
        const res = await fetch(`${API}/matters`, {
          headers: { Authorization: `Bearer ${t}` }
        });
        const data = await res.json();
        const matters = data.matters || [];

        // Flatten runs across matters for billing display
        // In future this will be a dedicated /account/runs endpoint
        setRuns(matters.flatMap(m => {
          const r = [];
          if (m.last_run_id && m.litfit_score != null) {
            r.push({
              run_id:      m.last_run_id,
              matter_name: m.matter_name,
              matter_id:   m.matter_id,
              litfit_score: m.litfit_score,
              run_status:  m.run_status,
              amount_aud:  50.00,
              created_at:  m.updated_at,
            });
          }
          return r;
        }));

        const count = matters.filter(m => m.last_run_id).length;
        setTotals({ count, spend: count * 50 });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#F7F5F0', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ background: '#FFF', borderBottom: '1px solid #E8E4DC', padding: '22px 32px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#AAA', fontSize: 20, lineHeight: 1, padding: 0 }}
        >←</button>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 500, color: DARK, flex: 1 }}>
          Account
        </div>
        <button
          onClick={onSignOut}
          style={{ background: 'none', border: '1px solid #E0DDD6', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#888', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
        >Sign out</button>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: 800 }}>

        {/* User info */}
        <Section title="Profile">
          <Row label="Email" value={user?.name || user?.email || '—'} />
          <Row label="Practitioner type" value="External solicitor" />
          <Row label="T&Cs accepted" value="v1.0" />
        </Section>

        {/* Billing summary */}
        <Section title="Billing">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <StatCard label="Total runs" value={totals.count} />
            <StatCard label="Total spend" value={`$${totals.spend.toFixed(2)} AUD`} />
          </div>
          <div style={{ fontSize: 12, color: '#AAA', marginBottom: 8 }}>
            Each Case Readiness Report is charged at $50.00 AUD at the time of the run. Charges appear on your statement as CASE READINESS REPORT.
          </div>
        </Section>

        {/* Run history */}
        <Section title="Run history">
          {loading ? (
            <div style={{ fontSize: 13, color: '#BBB', padding: '20px 0' }}>Loading…</div>
          ) : runs.length === 0 ? (
            <div style={{ fontSize: 13, color: '#BBB', padding: '20px 0' }}>No runs yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E8E4DC' }}>
                  {['Matter', 'Date', 'LitFit™', 'Amount'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#BBB', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((r, i) => (
                  <tr key={r.run_id} style={{ borderBottom: '1px solid #F0EDE7', background: i % 2 === 0 ? '#FFF' : '#FAFAF8' }}>
                    <td style={{ padding: '10px 12px', color: '#333' }}>{r.matter_name}</td>
                    <td style={{ padding: '10px 12px', color: '#888' }}>
                      {new Date(r.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '10px 12px', color: r.litfit_score ? scoreColor(r.litfit_score) : '#AAA', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500 }}>
                      {r.litfit_score?.toFixed(1) ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#333' }}>
                      ${r.amount_aud?.toFixed(2) ?? '50.00'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Security */}
        <Section title="Security">
          <Row label="Two-factor authentication" value="Coming soon" />
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#AAA', lineHeight: 1.6 }}>
              Two-factor authentication adds an extra layer of security for your client matter data. We're adding it as an account option shortly after launch.
            </div>
          </div>
        </Section>

        {/* Data & Privacy */}
        <Section title="Data & Privacy">
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 12 }}>
            Your matter data is stored securely in AWS Sydney (ap-southeast-2). Matter material and Case Readiness Reports are automatically deleted 30 days after each run, or immediately when you delete the matter. Keep your own copy of anything you need to retain. You may request deletion at any time.
          </div>
          <div style={{ fontSize: 12, color: '#AAA' }}>
            Privacy enquiries: privacy@calderbank.ai
          </div>
        </Section>

      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: '#BBB', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #E8E4DC' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5F2EE', fontSize: 13 }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ color: '#333' }}>{value}</span>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#FFF', border: '1px solid #E8E4DC', borderRadius: 10, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, color: '#BBB', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 500, color: DARK }}>{value}</div>
    </div>
  );
}
